/**
 * Single source of truth for backend property-enum → user-facing label
 * mappings. Update this file when the backend introduces a new enum value
 * or when a label needs retitling — do not hard-code labels in views.
 *
 * Consumers:
 *   - {@link ../utils/propertyUtils.js} — property table subtitle and detail grid
 *   - {@link ../services/apv/utils.ts}  — APV archetype chart axis labels
 *   - {@link ../components/filters/optionsCache.js} — filter dropdown options
 */

export const PROPERTY_TYPE_LABELS = {
  FLAT: 'Flat',
  HOUSE: 'House',
  BUNGALOW: 'Bungalow',
  UNKNOWN: 'Unknown'
} as const

export type PropertyTypeEnum = keyof typeof PROPERTY_TYPE_LABELS

export const PROPERTY_FORM_LABELS = {
  DETACHED: 'Detached',
  SEMIDETACHED: 'Semi-detached',
  TERRACED: 'Terraced',
  ENDTERRACE: 'End-terrace',
  UNKNOWN: 'Unknown'
} as const

export type PropertyFormEnum = keyof typeof PROPERTY_FORM_LABELS

export const PROPERTY_AGE_LABELS = {
  BEFORE_1900: 'Pre-1900',
  YEAR_1900_1929: '1900-1929',
  YEAR_1930_1949: '1930-1949',
  YEAR_1950_1966: '1950-1966',
  YEAR_1967_1975: '1967-1975',
  YEAR_1976_1982: '1976-1982',
  YEAR_1983_1990: '1983-1990',
  YEAR_1991_1995: '1991-1995',
  YEAR_1996_2002: '1996-2002',
  YEAR_2003_2006: '2003-2006',
  YEAR_2007_2011: '2007-2011',
  AFTER_2012: '2012 onwards'
} as const

export type PropertyAgeEnum = keyof typeof PROPERTY_AGE_LABELS

/**
 * Combined map used by the APV "archetype" feature, where a single chart
 * axis can carry either a property type or a property form depending on
 * the row. Union of {@link PROPERTY_TYPE_LABELS} and
 * {@link PROPERTY_FORM_LABELS}.
 */
export const ARCHETYPE_LABELS = {
  ...PROPERTY_TYPE_LABELS,
  ...PROPERTY_FORM_LABELS
} as const

export type ArchetypeEnum = keyof typeof ARCHETYPE_LABELS
export type ArchetypeLabel = (typeof ARCHETYPE_LABELS)[ArchetypeEnum]

/** Sentinel returned by the backend for an unknown enum case. */
export const UNKNOWN_ENUM_LABEL = 'Unknown'
